import React, { useState, useMemo, memo, useRef, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from 'react-simple-maps';
import { scaleLinear, scaleSqrt } from 'd3-scale';
import { Users, MapPin, TrendingUp, DollarSign } from 'lucide-react';

const WORLD_GEO = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Major city coordinates [lon, lat]
const CITY_COORDS = {
  // India
  'mumbai': [72.8777, 19.0760], 'delhi': [77.1025, 28.7041], 'new delhi': [77.2090, 28.6139],
  'bangalore': [77.5946, 12.9716], 'bengaluru': [77.5946, 12.9716], 'chennai': [80.2707, 13.0827],
  'hyderabad': [78.4867, 17.3850], 'kolkata': [88.3639, 22.5726], 'pune': [73.8567, 18.5204],
  'ahmedabad': [72.5714, 23.0225], 'jaipur': [75.7873, 26.9124], 'surat': [72.8311, 21.1702],
  'lucknow': [80.9462, 26.8467], 'kanpur': [80.3319, 26.4499], 'nagpur': [79.0882, 21.1458],
  'indore': [75.8577, 22.7196], 'thane': [72.9781, 19.2183], 'bhopal': [77.4126, 23.2599],
  'visakhapatnam': [83.2185, 17.6868], 'patna': [85.1376, 25.6093], 'vadodara': [73.1812, 22.3072],
  'gurgaon': [77.0266, 28.4595], 'gurugram': [77.0266, 28.4595], 'noida': [77.3910, 28.5355],
  'kochi': [76.2673, 9.9312], 'coimbatore': [76.9558, 11.0168], 'chandigarh': [76.7794, 30.7333],
  'navi mumbai': [73.0169, 19.0330], 'rajkot': [70.8022, 22.3039], 'amritsar': [74.8723, 31.6340],
  // South Asia (other)
  'colombo': [79.8612, 6.9271], 'dhaka': [90.4125, 23.8103], 'kathmandu': [85.3240, 27.7172],
  'islamabad': [73.0479, 33.6844], 'karachi': [67.0011, 24.8607], 'lahore': [74.3587, 31.5204],
  // Southeast Asia
  'singapore': [103.8198, 1.3521], 'kuala lumpur': [101.6869, 3.1390], 'bangkok': [100.5018, 13.7563],
  'jakarta': [106.8456, -6.2088], 'manila': [120.9842, 14.5995], 'ho chi minh city': [106.6297, 10.8231],
  'hanoi': [105.8342, 21.0285], 'phnom penh': [104.9282, 11.5564], 'yangon': [96.1561, 16.8409],
  // Middle East
  'dubai': [55.2708, 25.2048], 'abu dhabi': [54.3773, 24.4539], 'riyadh': [46.6753, 24.7136],
  'doha': [51.5310, 25.2854], 'muscat': [58.5378, 23.5859], 'manama': [50.5860, 26.2235],
  'kuwait city': [47.9774, 29.3759], 'tel aviv': [34.7818, 32.0853], 'amman': [35.9106, 31.9544],
  'beirut': [35.5018, 33.8938],
  // Africa
  'johannesburg': [28.0473, -26.2041], 'cape town': [18.4241, -33.9249], 'nairobi': [36.8219, -1.2921],
  'lagos': [3.3792, 6.5244], 'cairo': [31.2357, 30.0444], 'casablanca': [-7.5898, 33.5731],
  'accra': [-0.1870, 5.6037], 'addis ababa': [38.7578, 9.0301], 'port louis': [57.5012, -20.1609],
  // Other (Americas / Europe / Asia-Pacific)
  'new york': [-74.006, 40.7128], 'london': [-0.1276, 51.5074], 'hong kong': [114.1694, 22.3193],
  'tokyo': [139.6917, 35.6895], 'san francisco': [-122.4194, 37.7749], 'sydney': [151.2093, -33.8688],
  'toronto': [-79.3832, 43.6532], 'zurich': [8.5417, 47.3769], 'frankfurt': [8.6821, 50.1109],
  'paris': [2.3522, 48.8566], 'shanghai': [121.4737, 31.2304], 'seoul': [126.9780, 37.5665],
  'amsterdam': [4.9041, 52.3676], 'dublin': [-6.2603, 53.3498], 'luxembourg': [6.1296, 49.8153],
};

// Region definitions: lon/lat bounding boxes + map view config
const REGIONS = {
  all: { label: 'All Regions', center: [20, 10], zoom: 1, bbox: null },
  south_asia: { label: 'South Asia', center: [80, 22], zoom: 3, bbox: [60, 5, 100, 38] },
  southeast_asia: { label: 'Southeast Asia', center: [110, 5], zoom: 3, bbox: [92, -10, 140, 25] },
  middle_east: { label: 'Middle East', center: [50, 28], zoom: 3, bbox: [30, 12, 65, 42] },
  africa: { label: 'Africa', center: [20, 5], zoom: 1.7, bbox: [-20, -35, 55, 38] },
  other: { label: 'Other', center: [0, 35], zoom: 1.3, bbox: null },
};

const inBBox = (coords, bbox) => {
  if (!bbox) return true;
  const [lon, lat] = coords;
  const [minLon, minLat, maxLon, maxLat] = bbox;
  return lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat;
};

const fmt = (val) => {
  if (!val) return '0';
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)} K`;
  return val.toLocaleString('en-IN');
};

// Simple distance-based clustering: groups points within `gridDeg` of each other
const clusterPoints = (points, gridDeg) => {
  if (gridDeg <= 0) return points.map(p => ({ ...p, isCluster: false, members: [p] }));
  const grid = {};
  points.forEach(p => {
    const gx = Math.round(p.coordinates[0] / gridDeg);
    const gy = Math.round(p.coordinates[1] / gridDeg);
    const key = `${gx}_${gy}`;
    if (!grid[key]) grid[key] = [];
    grid[key].push(p);
  });
  return Object.values(grid).map(group => {
    if (group.length === 1) return { ...group[0], isCluster: false, members: group };
    const totalInvestors = group.reduce((s, g) => s + (g.investors || 0), 0);
    const totalCommitment = group.reduce((s, g) => s + (g.commitment || 0), 0);
    const totalContribution = group.reduce((s, g) => s + (g.contribution || 0), 0);
    const avgLon = group.reduce((s, g) => s + g.coordinates[0], 0) / group.length;
    const avgLat = group.reduce((s, g) => s + g.coordinates[1], 0) / group.length;
    return {
      name: `${group.length} cities`,
      coordinates: [avgLon, avgLat],
      investors: totalInvestors,
      commitment: totalCommitment,
      contribution: totalContribution,
      isCluster: true,
      members: group,
    };
  });
};

// ─── World Map Widget (Redesigned) ───
export const WorldMapWidget = memo(({ geoData, countryData, metric = 'commitment' }) => {
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedMetric, setSelectedMetric] = useState(metric);
  const [region, setRegion] = useState('all');
  const [mapPosition, setMapPosition] = useState({ coordinates: REGIONS.all.center, zoom: REGIONS.all.zoom });
  const [containerWidth, setContainerWidth] = useState(800);
  const containerRef = useRef(null);

  // Track container width for responsive side panel
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Build city markers from geoData (group by case-insensitive city name to dedupe)
  const allCityMarkers = useMemo(() => {
    const groups = {};
    (geoData || []).forEach(g => {
      const cityKey = (g.city || '').toLowerCase().trim();
      if (!cityKey) return;
      const coords = CITY_COORDS[cityKey];
      if (!coords) return;
      if (!groups[cityKey]) {
        groups[cityKey] = {
          name: g.city,
          state: g.state,
          country: g.country,
          coordinates: coords,
          investors: 0,
          commitment: 0,
          contribution: 0,
        };
      }
      groups[cityKey].investors += g.investors || 0;
      groups[cityKey].commitment += g.commitment || 0;
      groups[cityKey].contribution += g.contribution || 0;
    });
    return Object.values(groups).map(m => ({
      ...m,
      avg_contribution: m.investors > 0 ? m.contribution / m.investors : 0,
    }));
  }, [geoData]);

  // Filter by region
  const regionalMarkers = useMemo(() => {
    const cfg = REGIONS[region];
    return allCityMarkers.filter(m => inBBox(m.coordinates, cfg.bbox));
  }, [allCityMarkers, region]);

  // Cluster based on zoom level (more zoom = less clustering); skip if very few markers
  const clusteredMarkers = useMemo(() => {
    if (regionalMarkers.length <= 5) {
      return regionalMarkers.map(p => ({ ...p, isCluster: false, members: [p] }));
    }
    const z = mapPosition.zoom;
    const gridDeg = z >= 4 ? 0 : z >= 2.5 ? 1.2 : z >= 1.5 ? 3 : 5.5;
    return clusterPoints(regionalMarkers, gridDeg);
  }, [regionalMarkers, mapPosition.zoom]);

  // Color scale (blue gradient, matching legend)
  const maxVal = useMemo(() =>
    Math.max(...regionalMarkers.map(m => m[selectedMetric] || 0), 1),
    [regionalMarkers, selectedMetric]);

  const colorScale = useMemo(() =>
    scaleLinear()
      .domain([0, maxVal * 0.25, maxVal * 0.5, maxVal])
      .range(['#dbeafe', '#7dd3fc', '#2563eb', '#1e3a5f'])
      .clamp(true),
    [maxVal]);

  // Bubble size scale: based on investors count (sqrt for area-proportional)
  const maxInvestors = useMemo(() =>
    Math.max(...clusteredMarkers.map(m => m.investors || 0), 1),
    [clusteredMarkers]);

  const sizeScale = useMemo(() =>
    scaleSqrt().domain([0, maxInvestors]).range([4, 28]),
    [maxInvestors]);

  // Top 10 cities for leaderboard (by selected metric, not clustered)
  const top10 = useMemo(() => {
    return [...regionalMarkers]
      .sort((a, b) => (b[selectedMetric] || 0) - (a[selectedMetric] || 0))
      .slice(0, 10);
  }, [regionalMarkers, selectedMetric]);

  // KPIs
  const kpis = useMemo(() => {
    const totalInvestors = regionalMarkers.reduce((s, m) => s + (m.investors || 0), 0);
    const totalCommitment = regionalMarkers.reduce((s, m) => s + (m.commitment || 0), 0);
    const cities = regionalMarkers.length;
    const top = regionalMarkers.length ? [...regionalMarkers].sort((a, b) => (b.commitment || 0) - (a.commitment || 0))[0] : null;
    return {
      totalInvestors,
      cities,
      topCity: top ? top.name : '—',
      topCityValue: top ? top.commitment : 0,
      totalCommitment,
    };
  }, [regionalMarkers]);

  // Region change → recenter map
  const handleRegionChange = (key) => {
    setRegion(key);
    const cfg = REGIONS[key];
    setMapPosition({ coordinates: cfg.center, zoom: cfg.zoom });
  };

  // Click leaderboard row → fly to city
  const flyToCity = (city) => {
    setMapPosition({ coordinates: city.coordinates, zoom: 5 });
    setTooltip({ ...city, type: 'city' });
  };

  const showSidePanel = containerWidth >= 640;
  const metricLabel = { commitment: 'Commitment', contribution: 'Contribution', investors: 'Investors' }[selectedMetric];

  return (
    <div ref={containerRef} className="h-full flex flex-col gap-2" data-testid="world-map-widget">
      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/60 border border-blue-100 rounded-lg px-2.5 py-1.5" data-testid="kpi-total-investors">
          <div className="flex items-center gap-1.5">
            <Users size={12} className="text-blue-600" />
            <span className="text-[10px] uppercase tracking-wide text-blue-700/80 font-medium">Investors</span>
          </div>
          <p className="text-base font-bold text-blue-900 leading-tight">{kpis.totalInvestors.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/60 border border-indigo-100 rounded-lg px-2.5 py-1.5" data-testid="kpi-cities">
          <div className="flex items-center gap-1.5">
            <MapPin size={12} className="text-indigo-600" />
            <span className="text-[10px] uppercase tracking-wide text-indigo-700/80 font-medium">Cities</span>
          </div>
          <p className="text-base font-bold text-indigo-900 leading-tight">{kpis.cities}</p>
        </div>
        <div className="bg-gradient-to-br from-violet-50 to-violet-100/60 border border-violet-100 rounded-lg px-2.5 py-1.5" data-testid="kpi-top-city">
          <div className="flex items-center gap-1.5">
            <TrendingUp size={12} className="text-violet-600" />
            <span className="text-[10px] uppercase tracking-wide text-violet-700/80 font-medium">Top City</span>
          </div>
          <p className="text-sm font-bold text-violet-900 leading-tight truncate" title={kpis.topCity}>{kpis.topCity}</p>
        </div>
        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100/60 border border-cyan-100 rounded-lg px-2.5 py-1.5" data-testid="kpi-total-commitment">
          <div className="flex items-center gap-1.5">
            <DollarSign size={12} className="text-cyan-600" />
            <span className="text-[10px] uppercase tracking-wide text-cyan-700/80 font-medium">Commitment</span>
          </div>
          <p className="text-base font-bold text-cyan-900 leading-tight">{fmt(kpis.totalCommitment)}</p>
        </div>
      </div>

      {/* Region tabs + metric toggle */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
        <div className="flex flex-wrap gap-1" data-testid="region-tabs">
          {Object.entries(REGIONS).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => handleRegionChange(key)}
              className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors ${
                region === key ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`region-${key}`}
            >
              {cfg.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-500 mr-0.5">Color by:</span>
          {[
            { key: 'commitment', label: 'Commitment' },
            { key: 'contribution', label: 'Contribution' },
            { key: 'investors', label: 'Investors' },
          ].map(m => (
            <button
              key={m.key}
              onClick={() => setSelectedMetric(m.key)}
              className={`text-[11px] px-2 py-0.5 rounded-full transition-colors ${
                selectedMetric === m.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`map-metric-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Map + Side panel */}
      <div className="flex-1 flex gap-2" style={{ minHeight: 0 }}>
        <div className="flex-1 relative bg-slate-50 rounded-lg overflow-hidden border border-slate-200" style={{ minHeight: 0 }}>
          <ComposableMap
            projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }}
            style={{ width: '100%', height: '100%' }}
          >
            <ZoomableGroup
              center={mapPosition.coordinates}
              zoom={mapPosition.zoom}
              maxZoom={8}
              minZoom={0.8}
              onMoveEnd={(pos) => setMapPosition(pos)}
            >
              <Geographies geography={WORLD_GEO}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#e5e7eb"
                      stroke="#d1d5db"
                      strokeWidth={0.3}
                      style={{
                        default: { outline: 'none' },
                        hover: { outline: 'none', fill: '#d1d5db' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>

              {/* Bubble markers */}
              {clusteredMarkers.map((m, i) => {
                const val = m[selectedMetric] || 0;
                const r = sizeScale(m.investors || 1);
                const fill = colorScale(val);
                return (
                  <Marker
                    key={`${m.name}_${i}`}
                    coordinates={m.coordinates}
                    onMouseEnter={(e) => {
                      setTooltip({ ...m, type: m.isCluster ? 'cluster' : 'city' });
                      setTooltipPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setTooltip(null)}
                    onClick={() => {
                      if (m.isCluster) {
                        // expand cluster: zoom into the area
                        setMapPosition({ coordinates: m.coordinates, zoom: Math.min(8, mapPosition.zoom * 2) });
                      } else {
                        flyToCity(m);
                      }
                    }}
                  >
                    <circle
                      r={r}
                      fill={fill}
                      fillOpacity={0.85}
                      stroke="#1e3a5f"
                      strokeWidth={m.isCluster ? 1.5 : 0.8}
                      strokeDasharray={m.isCluster ? '2,1.5' : 'none'}
                      style={{ cursor: 'pointer', transition: 'r 0.2s ease' }}
                    />
                    {m.isCluster && (
                      <text
                        textAnchor="middle"
                        dy="0.3em"
                        style={{
                          fontSize: Math.max(8, r / 1.8),
                          fill: val > maxVal * 0.4 ? '#fff' : '#1e3a5f',
                          fontWeight: 700,
                          pointerEvents: 'none',
                          userSelect: 'none',
                        }}
                      >
                        {m.members.length}
                      </text>
                    )}
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>

          {/* Floating Tooltip (mouse-anchored) */}
          {tooltip && (
            <div
              className="fixed bg-white border border-gray-200 rounded-lg shadow-xl p-2.5 z-50 pointer-events-none"
              style={{
                left: Math.min(tooltipPos.x + 12, window.innerWidth - 240),
                top: Math.min(tooltipPos.y + 12, window.innerHeight - 140),
                minWidth: 200,
              }}
              data-testid="map-tooltip"
            >
              <p className="font-semibold text-gray-900 text-sm mb-1">
                {tooltip.isCluster ? `${tooltip.members.length} cities` : (
                  <>
                    {tooltip.name}
                    {tooltip.country && <span className="text-gray-500 font-normal text-xs"> · {tooltip.country}</span>}
                  </>
                )}
              </p>
              <div className="space-y-0.5 text-xs">
                <p className="flex justify-between gap-3"><span className="text-gray-500">Investors:</span><span className="font-medium">{(tooltip.investors || 0).toLocaleString('en-IN')}</span></p>
                <p className="flex justify-between gap-3"><span className="text-gray-500">Commitment:</span><span className="font-mono font-medium">{fmt(tooltip.commitment)}</span></p>
                <p className="flex justify-between gap-3"><span className="text-gray-500">Avg / Investor:</span><span className="font-mono font-medium">{fmt(tooltip.avg_contribution || (tooltip.investors ? tooltip.contribution / tooltip.investors : 0))}</span></p>
              </div>
              {tooltip.isCluster && (
                <p className="text-[10px] text-blue-600 mt-1.5 italic">Click to zoom in</p>
              )}
            </div>
          )}

          {/* Legend */}
          <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur-sm rounded-md px-2 py-1 border border-gray-200 text-[10px] shadow-sm">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500 font-medium">Low</span>
              <div className="flex h-2 rounded-sm overflow-hidden">
                {['#dbeafe', '#7dd3fc', '#2563eb', '#1e3a5f'].map(c => (
                  <div key={c} style={{ backgroundColor: c, width: 16, height: 8 }} />
                ))}
              </div>
              <span className="text-gray-500 font-medium">High</span>
              <span className="text-gray-400 ml-1">· {metricLabel}</span>
            </div>
          </div>

          {/* Reset button */}
          {(mapPosition.zoom !== REGIONS[region].zoom) && (
            <button
              onClick={() => handleRegionChange(region)}
              className="absolute top-2 right-2 bg-white/95 hover:bg-white border border-gray-200 rounded-md px-2 py-1 text-[10px] font-medium text-gray-700 shadow-sm"
              data-testid="reset-map"
            >
              Reset View
            </button>
          )}
        </div>

        {/* Side leaderboard */}
        {showSidePanel && (
          <div className="w-[260px] flex-shrink-0 bg-white border border-slate-200 rounded-lg flex flex-col" data-testid="map-leaderboard">
            <div className="px-3 py-2 border-b border-slate-100">
              <p className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Top 10 Cities</p>
              <p className="text-[10px] text-gray-400">By {metricLabel.toLowerCase()}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1" style={{ minHeight: 0 }}>
              {top10.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No cities in this region</p>
              ) : top10.map((c, i) => {
                const val = c[selectedMetric] || 0;
                const pct = (val / (top10[0][selectedMetric] || 1)) * 100;
                return (
                  <button
                    key={`${c.name}_${i}`}
                    onClick={() => flyToCity(c)}
                    className="w-full text-left p-2 rounded-md hover:bg-blue-50 group transition-colors"
                    data-testid={`leaderboard-row-${i}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[10px] font-bold text-gray-400 w-3 text-right">{i + 1}</span>
                        <span className="text-xs font-semibold text-gray-800 truncate group-hover:text-blue-700">{c.name}</span>
                      </div>
                      <span className="text-[10px] font-mono text-gray-600 ml-1">
                        {selectedMetric === 'investors' ? c.investors : fmt(val)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: colorScale(val) }}
                        />
                      </div>
                      <span className="text-[9px] text-gray-400 w-12 text-right">{c.investors} inv.</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

WorldMapWidget.displayName = 'WorldMapWidget';

// ─── State Heatmap (India-centric bar + map hybrid) ───
export const StateHeatmapWidget = memo(({ stateData, metric = 'commitment' }) => {
  const [selectedMetric, setSelectedMetric] = useState(metric);
  const [hoveredState, setHoveredState] = useState(null);

  const sortedData = useMemo(() => {
    return [...(stateData || [])].sort((a, b) => (b[selectedMetric] || 0) - (a[selectedMetric] || 0));
  }, [stateData, selectedMetric]);

  const maxVal = useMemo(() => Math.max(...sortedData.map(s => s[selectedMetric] || 0), 1), [sortedData, selectedMetric]);

  const colorScale = scaleLinear()
    .domain([0, maxVal * 0.3, maxVal * 0.6, maxVal])
    .range(['#dcfce7', '#86efac', '#16a34a', '#14532d']);

  return (
    <div className="h-full flex flex-col" data-testid="state-heatmap-widget">
      <div className="flex items-center gap-2 mb-2 px-1">
        <span className="text-xs text-gray-500">Metric:</span>
        {[{ key: 'commitment', label: 'Commitment' }, { key: 'contribution', label: 'Contribution' }, { key: 'investors', label: 'Count' }].map(m => (
          <button key={m.key} onClick={() => setSelectedMetric(m.key)}
            className={`text-xs px-2 py-0.5 rounded-full transition-colors ${selectedMetric === m.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto space-y-1 px-1" style={{ minHeight: 0 }}>
        {sortedData.map((s, i) => {
          const val = s[selectedMetric] || 0;
          const isHovered = hoveredState === s.name;
          return (
            <div key={s.name}
              className={`relative rounded-lg overflow-hidden transition-all cursor-pointer ${isHovered ? 'ring-2 ring-emerald-500' : ''}`}
              onMouseEnter={() => setHoveredState(s.name)}
              onMouseLeave={() => setHoveredState(null)}
              style={{ height: sortedData.length > 8 ? 28 : 36 }}>
              <div className="absolute inset-0 rounded-lg" style={{ backgroundColor: colorScale(val), opacity: 0.9 }} />
              <div className="relative flex items-center justify-between px-3 h-full">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500 w-4">{i + 1}</span>
                  <span className={`text-sm font-medium ${val > maxVal * 0.5 ? 'text-white' : 'text-gray-800'}`}>{s.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs ${val > maxVal * 0.5 ? 'text-white/80' : 'text-gray-500'}`}>
                    {s.investors} investor{s.investors !== 1 ? 's' : ''}
                  </span>
                  <span className={`text-sm font-bold font-mono ${val > maxVal * 0.5 ? 'text-white' : 'text-gray-900'}`}>
                    {fmt(val)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        {!sortedData.length && <p className="text-gray-400 text-sm text-center py-4">No state data available</p>}
      </div>
    </div>
  );
});

StateHeatmapWidget.displayName = 'StateHeatmapWidget';

// ─── City Heatmap ───
export const CityHeatmapWidget = memo(({ cityData, metric = 'commitment' }) => {
  const [selectedMetric, setSelectedMetric] = useState(metric);

  const sortedData = useMemo(() => {
    return [...(cityData || [])].sort((a, b) => (b[selectedMetric] || 0) - (a[selectedMetric] || 0));
  }, [cityData, selectedMetric]);

  const maxVal = useMemo(() => Math.max(...sortedData.map(s => s[selectedMetric] || 0), 1), [sortedData, selectedMetric]);

  const colorScale = scaleLinear()
    .domain([0, maxVal * 0.3, maxVal * 0.6, maxVal])
    .range(['#fef3c7', '#fbbf24', '#d97706', '#78350f']);

  return (
    <div className="h-full flex flex-col" data-testid="city-heatmap-widget">
      <div className="flex items-center gap-2 mb-2 px-1">
        <span className="text-xs text-gray-500">Metric:</span>
        {[{ key: 'commitment', label: 'Commitment' }, { key: 'contribution', label: 'Contribution' }, { key: 'investors', label: 'Count' }].map(m => (
          <button key={m.key} onClick={() => setSelectedMetric(m.key)}
            className={`text-xs px-2 py-0.5 rounded-full transition-colors ${selectedMetric === m.key ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto space-y-1 px-1" style={{ minHeight: 0 }}>
        {sortedData.map((s, i) => {
          const val = s[selectedMetric] || 0;
          return (
            <div key={s.name} className="relative rounded-lg overflow-hidden" style={{ height: sortedData.length > 8 ? 28 : 36 }}>
              <div className="absolute inset-0 rounded-lg" style={{ backgroundColor: colorScale(val), opacity: 0.85 }} />
              <div className="relative flex items-center justify-between px-3 h-full">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500 w-4">{i + 1}</span>
                  <span className={`text-sm font-medium ${val > maxVal * 0.5 ? 'text-white' : 'text-gray-800'}`}>{s.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs ${val > maxVal * 0.5 ? 'text-white/80' : 'text-gray-500'}`}>
                    {s.investors} investor{s.investors !== 1 ? 's' : ''}
                  </span>
                  <span className={`text-sm font-bold font-mono ${val > maxVal * 0.5 ? 'text-white' : 'text-gray-900'}`}>
                    {fmt(val)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        {!sortedData.length && <p className="text-gray-400 text-sm text-center py-4">No city data available</p>}
      </div>
    </div>
  );
});

CityHeatmapWidget.displayName = 'CityHeatmapWidget';
